import { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  X,
  Check,
  AlertTriangle,
  UserMinus,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { DetailedUser, OffboardingOperations, OffboardingResponse, TenantUser } from '../../types';
import { offboardUser, getClientUsers, checkOffboardExchangeStatus } from '../../services/api';

interface OffboardWizardProps {
  clientId: string;
  user: DetailedUser;
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

type WizardStep = 'confirm' | 'operations' | 'delegate' | 'onedrive' | 'review' | 'progress';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function OffboardWizard({ clientId, user, isOpen, onClose, onComplete }: OffboardWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('confirm');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OffboardingResponse | null>(null);
  const [exchangeStatus, setExchangeStatus] = useState<'pending' | 'running' | 'completed' | 'failed' | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll Exchange operation status when offboarding has pending Exchange ops
  useEffect(() => {
    if (result?.exchangeOperationsPending && result.offboardingId) {
      setExchangeStatus('running');
      let attempts = 0;
      const maxAttempts = 18; // 3 minutes at 10s intervals

      pollTimerRef.current = setInterval(async () => {
        attempts++;
        try {
          const status = await checkOffboardExchangeStatus(result.offboardingId);
          if (status.exchangeStatus === 'completed') {
            setExchangeStatus('completed');
            toast.success('Exchange operations completed successfully', { duration: 6000 });
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          } else if (status.exchangeStatus === 'failed') {
            setExchangeStatus('failed');
            toast.error(`Exchange operations failed: ${status.exchangeError || 'Unknown error'}`, { duration: 8000 });
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          } else if (attempts >= maxAttempts) {
            setExchangeStatus('failed');
            toast.error('Exchange operations timed out — check Azure Automation for job status', { duration: 8000 });
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          }
        } catch {
          // Silent retry on network errors
        }
      }, 10000);
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [result?.exchangeOperationsPending, result?.offboardingId]);

  // Form state
  const [operations, setOperations] = useState<OffboardingOperations>({
    disableAccount: true,
    revokeSession: true,
    resetPassword: true,
    removeLicenses: true,
    removeFromGroups: true,
    convertToShared: false,
    grantMailboxAccess: false,
    hideFromGAL: false,
    shareOneDrive: false,
  });
  const [delegateEmails, setDelegateEmails] = useState<string[]>([]);
  const [delegateInput, setDelegateInput] = useState('');
  const [oneDriveAccessLevel, setOneDriveAccessLevel] = useState<'read' | 'write' | 'owner'>('read');
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load tenant users when we need to show delegate selection
  const loadTenantUsers = async () => {
    if (tenantUsers.length > 0) return;
    setLoadingUsers(true);
    try {
      const response = await getClientUsers(clientId);
      setTenantUsers(response.users.filter(u => u.id !== user.id));
    } catch (err) {
      console.error('Failed to load tenant users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const needsDelegate = Boolean(operations.grantMailboxAccess || operations.shareOneDrive);
  const needsOneDriveConfig = Boolean(operations.shareOneDrive);

  const steps: { key: WizardStep; label: string; show: boolean }[] = [
    { key: 'confirm', label: 'Confirm User', show: true },
    { key: 'operations', label: 'Select Operations', show: true },
    { key: 'delegate', label: 'Select Delegate', show: needsDelegate },
    { key: 'onedrive', label: 'OneDrive Access', show: needsOneDriveConfig },
    { key: 'review', label: 'Review', show: true },
    { key: 'progress', label: 'Complete', show: true },
  ];

  const visibleSteps = steps.filter(s => s.show);
  const currentStepIndex = visibleSteps.findIndex(s => s.key === currentStep);

  const goNext = () => {
    const nextSteps = visibleSteps.slice(currentStepIndex + 1);
    if (nextSteps.length > 0) {
      if (nextSteps[0].key === 'delegate') {
        loadTenantUsers();
      }
      setCurrentStep(nextSteps[0].key);
    }
  };

  const goBack = () => {
    const prevSteps = visibleSteps.slice(0, currentStepIndex);
    if (prevSteps.length > 0) {
      setCurrentStep(prevSteps[prevSteps.length - 1].key);
    }
  };

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'confirm':
        return true;
      case 'operations':
        return Object.values(operations).some(v => v);
      case 'delegate':
        return delegateEmails.length > 0;
      case 'onedrive':
        return true;
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, operations, delegateEmails]);

  const handleExecute = async () => {
    setLoading(true);
    setCurrentStep('progress');
    try {
      const response = await offboardUser(clientId, user.id, {
        operations,
        delegateEmail: needsDelegate ? delegateEmails.join(',') : undefined,
        delegateEmails: needsDelegate ? delegateEmails : undefined,
        oneDriveAccessLevel: needsOneDriveConfig ? oneDriveAccessLevel : undefined,
      });
      setResult(response);
      if (response.success || response.status === 'partial') {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Offboarding failed');
      setResult({
        success: false,
        offboardingId: '',
        status: 'failed',
        user: { id: user.id, displayName: user.displayName, email: user.userPrincipalName },
        results: [],
        errors: [err instanceof Error ? err.message : 'Unknown error'],
        exchangeOperationsPending: false,
        message: 'Offboarding failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onComplete?.();
    }
    // Reset state
    setCurrentStep('confirm');
    setOperations({
      disableAccount: true,
      revokeSession: true,
      resetPassword: true,
      removeLicenses: true,
      removeFromGroups: true,
      convertToShared: false,
      grantMailboxAccess: false,
      hideFromGAL: false,
      shareOneDrive: false,
    });
    setDelegateEmails([]);
    setDelegateInput('');
    setOneDriveAccessLevel('read');
    setResult(null);
    setExchangeStatus(null);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    onClose();
  };

  const selectedOperationsCount = Object.values(operations).filter(Boolean).length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="p-0 gap-0 max-w-2xl [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        {/* Header */}
        <div className="bg-red-50 dark:bg-red-900/20 px-6 py-4 border-b border-red-200 dark:border-red-800 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UserMinus className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
              <DialogTitle className="text-lg font-semibold text-red-900 dark:text-red-100">
                Offboard User
              </DialogTitle>
            </div>
            <DialogDescription className="sr-only">Wizard to offboard a user by disabling their account, removing licenses, and managing data access</DialogDescription>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Step indicator */}
          {currentStep !== 'progress' && (
            <div className="mt-4 flex items-center space-x-2">
              {visibleSteps.filter(s => s.key !== 'progress').map((step, idx) => (
                <Fragment key={step.key}>
                  {idx > 0 && <div className="flex-1 h-0.5 bg-border" />}
                  <div
                    className={classNames(
                      'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                      step.key === currentStep
                        ? 'bg-red-600 text-white'
                        : idx < currentStepIndex
                        ? 'bg-green-500 text-white'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {idx < currentStepIndex ? <Check className="h-4 w-4" /> : idx + 1}
                  </div>
                </Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Confirm User */}
          {currentStep === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Warning: User Offboarding
                    </h3>
                    <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                      This will remove the user's access to company resources. Make sure you have verified
                      this action with the appropriate stakeholders.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-3">
                  User to be offboarded:
                </h4>
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                    <span className="text-red-700 dark:text-red-300 font-semibold">
                      {user.displayName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.displayName}</p>
                    <p className="text-sm text-muted-foreground">{user.userPrincipalName}</p>
                    {user.jobTitle && (
                      <p className="text-xs text-muted-foreground">{user.jobTitle}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Licenses:</span>
                    <span className="ml-2 text-foreground">{user.assignedLicenses.length}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Groups:</span>
                    <span className="ml-2 text-foreground">{user.groups.length}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Operations */}
          {currentStep === 'operations' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select the operations to perform during offboarding:
              </p>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Account Actions</h4>
                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.disableAccount}
                    onChange={e => setOperations({ ...operations, disableAccount: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Block sign-in</span>
                    <p className="text-xs text-muted-foreground">Prevent user from signing in to any Microsoft service</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.revokeSession}
                    onChange={e => setOperations({ ...operations, revokeSession: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Revoke active sessions</span>
                    <p className="text-xs text-muted-foreground">Force sign-out from all devices immediately</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.resetPassword}
                    onChange={e => setOperations({ ...operations, resetPassword: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Reset password</span>
                    <p className="text-xs text-muted-foreground">Change password to a random value</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.removeLicenses}
                    onChange={e => setOperations({ ...operations, removeLicenses: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Remove all licenses</span>
                    <p className="text-xs text-muted-foreground">Free up {user.assignedLicenses.length} license(s) for reuse</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.removeFromGroups}
                    onChange={e => setOperations({ ...operations, removeFromGroups: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Remove from all groups and distribution lists</span>
                    <p className="text-xs text-muted-foreground">Remove from {user.groups.length} group(s) including security groups, M365 groups, and distribution lists</p>
                  </div>
                </label>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-foreground">
                  Mailbox Actions
                  <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                    (Automated via Exchange Online)
                  </span>
                </h4>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.convertToShared}
                    onChange={e => setOperations({ ...operations, convertToShared: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Convert to shared mailbox</span>
                    <p className="text-xs text-muted-foreground">Retain mailbox without license cost</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.grantMailboxAccess}
                    onChange={e => setOperations({ ...operations, grantMailboxAccess: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Grant mailbox full access</span>
                    <p className="text-xs text-muted-foreground">Give delegate access to user's mailbox</p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.hideFromGAL}
                    onChange={e => setOperations({ ...operations, hideFromGAL: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Hide from address list</span>
                    <p className="text-xs text-muted-foreground">Remove from global address list (GAL)</p>
                  </div>
                </label>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-foreground">Data Actions</h4>

                <label className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={operations.shareOneDrive}
                    onChange={e => setOperations({ ...operations, shareOneDrive: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-red-600 border-border rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Share OneDrive with delegate</span>
                    <p className="text-xs text-muted-foreground">Grant access to user's OneDrive files</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 3: Select Delegates (multiple) */}
          {currentStep === 'delegate' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select delegates to receive access to the user's mailbox and/or OneDrive:
              </p>

              {/* Current delegates list */}
              {delegateEmails.length > 0 && (
                <div className="space-y-1">
                  {delegateEmails.map((email, i) => (
                    <div key={i} className="flex items-center justify-between bg-muted rounded-lg px-3 py-2">
                      <span className="text-sm text-foreground">{email}</span>
                      <button
                        onClick={() => setDelegateEmails(delegateEmails.filter((_, idx) => idx !== i))}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-foreground">
                    Add delegate
                  </label>
                  <div className="flex gap-2">
                    <select
                      value=""
                      onChange={e => {
                        if (e.target.value && !delegateEmails.includes(e.target.value)) {
                          setDelegateEmails([...delegateEmails, e.target.value]);
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                    >
                      <option value="">Select a user...</option>
                      {tenantUsers
                        .filter(u => !delegateEmails.includes(u.userPrincipalName))
                        .map(u => (
                          <option key={u.id} value={u.userPrincipalName}>
                            {u.displayName} ({u.userPrincipalName})
                          </option>
                        ))}
                    </select>
                  </div>

                  <p className="text-xs text-muted-foreground mt-2">
                    Or enter an email address manually:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={delegateInput}
                      onChange={e => setDelegateInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && delegateInput.includes('@') && !delegateEmails.includes(delegateInput.trim())) {
                          e.preventDefault();
                          setDelegateEmails([...delegateEmails, delegateInput.trim()]);
                          setDelegateInput('');
                        }
                      }}
                      placeholder="delegate@company.com"
                      className="flex-1 px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (delegateInput.includes('@') && !delegateEmails.includes(delegateInput.trim())) {
                          setDelegateEmails([...delegateEmails, delegateInput.trim()]);
                          setDelegateInput('');
                        }
                      }}
                      disabled={!delegateInput.includes('@')}
                      className="px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: OneDrive Access Level */}
          {currentStep === 'onedrive' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Choose the level of access to grant for OneDrive:
              </p>

              <div className="space-y-2">
                <label className="flex items-start space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="radio"
                    name="oneDriveAccess"
                    value="read"
                    checked={oneDriveAccessLevel === 'read'}
                    onChange={() => setOneDriveAccessLevel('read')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-border focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Read-only access</span>
                    <p className="text-xs text-muted-foreground">
                      Delegate can view and download files, but cannot modify or delete them
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="radio"
                    name="oneDriveAccess"
                    value="write"
                    checked={oneDriveAccessLevel === 'write'}
                    onChange={() => setOneDriveAccessLevel('write')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-border focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Edit access</span>
                    <p className="text-xs text-muted-foreground">
                      Delegate can view, edit, and organize files
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:bg-accent">
                  <input
                    type="radio"
                    name="oneDriveAccess"
                    value="owner"
                    checked={oneDriveAccessLevel === 'owner'}
                    onChange={() => setOneDriveAccessLevel('owner')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-border focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Transfer ownership</span>
                    <p className="text-xs text-muted-foreground">
                      Delegate becomes the owner of the files (full control)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">User</h4>
                <p className="text-sm text-muted-foreground">
                  {user.displayName} ({user.userPrincipalName})
                </p>
              </div>

              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Operations ({selectedOperationsCount})
                </h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {operations.disableAccount && <li>• Block sign-in</li>}
                  {operations.revokeSession && <li>• Revoke active sessions</li>}
                  {operations.resetPassword && <li>• Reset password</li>}
                  {operations.removeLicenses && <li>• Remove all licenses ({user.assignedLicenses.length})</li>}
                  {operations.removeFromGroups && <li>• Remove from all groups ({user.groups.length})</li>}
                  {operations.convertToShared && <li>• Convert to shared mailbox</li>}
                  {operations.grantMailboxAccess && <li>• Grant mailbox access to {delegateEmails.join(', ')}</li>}
                  {operations.hideFromGAL && <li>• Hide from address list</li>}
                  {operations.shareOneDrive && <li>• Share OneDrive ({oneDriveAccessLevel}) with {delegateEmails.join(', ')}</li>}
                </ul>
              </div>

              {(operations.convertToShared || operations.grantMailboxAccess || operations.hideFromGAL) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Exchange operations (convert to shared, grant access, hide from GAL) run via Azure Automation runbook and may take 30-90 seconds.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Progress/Complete */}
          {currentStep === 'progress' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                  <p className="text-sm text-muted-foreground">Executing offboarding operations...</p>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className={classNames(
                    'flex items-center justify-center p-4 rounded-lg',
                    result.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20' :
                    result.status === 'partial' ? 'bg-amber-50 dark:bg-amber-900/20' :
                    'bg-red-50 dark:bg-red-900/20'
                  )}>
                    {result.status === 'completed' ? (
                      <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                    ) : result.status === 'partial' ? (
                      <Clock className="h-8 w-8 text-amber-600 dark:text-amber-400 mr-3" />
                    ) : (
                      <XCircle className="h-8 w-8 text-red-600 dark:text-red-400 mr-3" />
                    )}
                    <div>
                      <h4 className={classNames(
                        'text-lg font-medium',
                        result.status === 'completed' ? 'text-green-800 dark:text-green-200' :
                        result.status === 'partial' ? 'text-amber-800 dark:text-amber-200' :
                        'text-red-800 dark:text-red-200'
                      )}>
                        {result.status === 'completed' ? 'Offboarding Complete' :
                         result.status === 'partial' ? 'Partially Complete' :
                         'Offboarding Failed'}
                      </h4>
                      <p className="text-sm text-muted-foreground">{result.message}</p>
                    </div>
                  </div>

                  {/* Results */}
                  {result.results.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">Completed</h5>
                      <ul className="space-y-1">
                        {result.results.map((r, i) => (
                          <li key={i} className="text-sm text-green-700 dark:text-green-300 flex items-center">
                            <Check className="h-4 w-4 mr-2" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Errors */}
                  {result.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Errors</h5>
                      <ul className="space-y-1">
                        {result.errors.map((e, i) => (
                          <li key={i} className="text-sm text-red-700 dark:text-red-300 flex items-center">
                            <XCircle className="h-4 w-4 mr-2" />
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Exchange Pending/Complete/Failed */}
                  {result.exchangeOperationsPending && (
                    <div className={`border rounded-lg p-4 ${
                      exchangeStatus === 'completed' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                      exchangeStatus === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                      'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}>
                      <div className="flex items-center mb-2">
                        {exchangeStatus === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                        ) : exchangeStatus === 'failed' ? (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                        ) : (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400 mr-2" />
                        )}
                        <h5 className={`text-sm font-medium ${
                          exchangeStatus === 'completed' ? 'text-green-800 dark:text-green-200' :
                          exchangeStatus === 'failed' ? 'text-red-800 dark:text-red-200' :
                          'text-blue-800 dark:text-blue-200'
                        }`}>
                          {exchangeStatus === 'completed' ? 'Exchange Operations Complete' :
                           exchangeStatus === 'failed' ? 'Exchange Operations Failed' :
                           'Exchange Operations In Progress...'}
                        </h5>
                      </div>
                      <ul className={`space-y-1 text-sm ${
                        exchangeStatus === 'completed' ? 'text-green-700 dark:text-green-300' :
                        exchangeStatus === 'failed' ? 'text-red-700 dark:text-red-300' :
                        'text-blue-700 dark:text-blue-300'
                      }`}>
                        {operations.convertToShared && <li>• Convert mailbox to shared</li>}
                        {operations.grantMailboxAccess && <li>• Grant full access to {delegateEmails.join(', ')}</li>}
                        {operations.hideFromGAL && <li>• Hide from address list</li>}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-muted px-6 py-4 border-t border-border rounded-b-lg">
          <div className="flex justify-between">
            <div>
              {currentStep !== 'confirm' && currentStep !== 'progress' && (
                <button
                  onClick={goBack}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-muted-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              {currentStep === 'progress' ? (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Done
                </button>
              ) : currentStep === 'review' ? (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExecute}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    <UserMinus className="h-4 w-4 mr-1" />
                    Execute Offboarding
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={goNext}
                    disabled={!canProceed}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
